"use client";
/*
 * QueryStatusBadge — Badge component for Query Status
 * Handles status variants: resolved (green), open (blue)
 */

import React from "react";
import * as SubframeCore from "@subframe/core";
import * as SubframeUtils from "../utils";

interface QueryStatusBadgeProps extends React.HTMLAttributes<HTMLDivElement> {
  status: "open" | "in_progress" | "closed" | "resolved";
  icon?: React.ReactNode;
  children?: React.ReactNode;
  iconRight?: React.ReactNode;
  className?: string;
}

const QueryStatusBadge = React.forwardRef<
  HTMLDivElement,
  QueryStatusBadgeProps
>(function QueryStatusBadge(
  {
    status,
    icon = null,
    children,
    iconRight = null,
    className,
    ...otherProps
  }: QueryStatusBadgeProps,
  ref
) {
  // Determine background and border colors based on status
  const getBackgroundClasses = () => {
    switch (status) {
      case "resolved":
        return "border-green-200 bg-green-100";
      case "open":
        return "border-blue-200 bg-blue-100";
      case "in_progress":
        return "border-yellow-200 bg-yellow-100";
      case "closed":
        return "border-gray-200 bg-gray-100";
      default:
        return "border-blue-200 bg-blue-100";
    }
  };

  // Determine text color based on status
  const getTextColor = () => {
    switch (status) {
      case "resolved":
        return "text-green-700";
      case "open":
        return "text-blue-700";
      case "in_progress":
        return "text-yellow-700";
      case "closed":
        return "text-gray-700";
      default:
        return "text-blue-700";
    }
  };

  // Determine icon color based on status
  const getIconColor = () => {
    switch (status) {
      case "resolved":
        return "text-green-700";
      case "open":
        return "text-blue-700";
      case "in_progress":
        return "text-yellow-700";
      case "closed":
        return "text-gray-700";
      default:
        return "text-blue-700";
    }
  };

  return (
    <div
      className={SubframeUtils.twClassNames(
        "group/query-status-badge flex h-6 items-center gap-1 rounded-md border border-solid px-2",
        getBackgroundClasses(),
        className
      )}
      ref={ref}
      {...otherProps}
    >
      {icon ? (
        <SubframeCore.IconWrapper
          className={SubframeUtils.twClassNames(
            "text-caption font-caption",
            getIconColor()
          )}
        >
          {icon}
        </SubframeCore.IconWrapper>
      ) : null}
      {children ? (
        <span
          className={SubframeUtils.twClassNames(
            "whitespace-nowrap text-caption font-caption",
            getTextColor()
          )}
        >
          {children}
        </span>
      ) : null}
      {iconRight ? (
        <SubframeCore.IconWrapper
          className={SubframeUtils.twClassNames(
            "text-caption font-caption",
            getIconColor()
          )}
        >
          {iconRight}
        </SubframeCore.IconWrapper>
      ) : null}
    </div>
  );
});

export { QueryStatusBadge };
